import { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { LocalStorageCache, parseAustralianPhoneNumber, useAuth0 } from '@brighte/passwordless-authenticator';
import { Header } from '@components/Header';
import { useDatadogAddError, usePrepop } from '@hooks';
import { Button } from '@spark-web/button';
import { Container } from '@spark-web/container';
import { Heading } from '@spark-web/heading';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { BRIGHTE_CONTACT_NUMBER } from '@utils/constants';
import { getTrackingId } from '@utils/helper';
import ErrorImage from '../../assets/error.svg?react';
import { getErrorDetails } from './helper';

export function ErrorFallback({ error }: FallbackProps) {
  const { user } = useAuth0();
  const { logError } = useDatadogAddError();
  const trackingId = getTrackingId();
  const { data } = usePrepop({ trackingId });
  const contactNumber = data?.enterprisePartnerConfig?.contactNumber ?? BRIGHTE_CONTACT_NUMBER;

  const { code, message } = getErrorDetails(error);
  const isUnauthenticated = message === 'UNAUTHENTICATED';

  const handleConfirm = () => {
    // Clear localStorage directly — NOT clearCredentials() — to avoid a React state
    // dispatch that resets the router error boundary and briefly mounts the Login
    // component (sending an OTP) before the page actually navigates away.
    const cacheKey = parseAustralianPhoneNumber(user?.phone_number);
    new LocalStorageCache().removeAll(cacheKey);
    const searchParams = new URLSearchParams(window.location.search);
    window.location.href = `/connect/login/?${searchParams.toString()}`;
  };

  useEffect(() => {
    logError(error);
  }, [logError, error]);

  return (
    <>
      {isUnauthenticated ? (
        <ContentDialog title="Login" isOpen={true} onToggle={handleConfirm}>
          <Text>
            Your session has expired or you have been logged out. Please log in again to continue the application.
          </Text>
          <Button onClick={handleConfirm}>Continue</Button>
        </ContentDialog>
      ) : (
        <Container size="medium">
          <Stack gap="xxlarge" padding="xlarge" marginBottom="xlarge">
            <Stack gap="xxlarge" alignSelf="center">
              <Header />
            </Stack>
            <Stack align="center" gap="xlarge" marginTop="xxlarge">
              <ErrorImage />
            </Stack>
            <Stack align="center" gap="xlarge">
              <Heading level="1">An error occurred</Heading>
              <Text align="center">{message}</Text>
              <Text>If you’re still experiencing issues, please call us on {contactNumber}</Text>
              {code && <Text tone="muted">Technical error {code}</Text>}
            </Stack>
          </Stack>
        </Container>
      )}
    </>
  );
}
